import { QueryClient } from '@tanstack/react-query';
import { nrPageAction } from '../util-helpers/newrelic';

/**
 * Creates and configures a TanStack Query client with error handling and logging
 */
export const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Default cache to 0.  We'll figure this out deeper later.
        staleTime: 0,
        cacheTime: 0,
        // Retry configuration
        retry: (failureCount, error: any) => {
          // Don't retry on 4xx errors (client errors)
          if (error?.response?.status >= 400 && error?.response?.status < 500) {
            return false;
          }
          // Retry up to 3 times for other errors
          return failureCount < 3;
        },
        // Retry delay with exponential backoff
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch on window focus (disabled for Outlook add-in context)
        refetchOnWindowFocus: false,
        // Refetch on reconnect
        refetchOnReconnect: true,
        // Error handling
        onError: (error: any) => {
          console.error('Query Error:', error);
          nrPageAction('queryClient', {
            message: `Query failed: ${error?.message || 'Unknown error'}`,
            url: window.location.href,
            error: error?.response?.status || 'unknown'
          });
        },
      },
      mutations: {
        // Retry mutations once for network errors only
        retry: (failureCount, error: any) => {
          // Only retry network errors, not client errors
          if (error?.response?.status >= 400 && error?.response?.status < 500) {
            return false;
          }
          return failureCount < 1;
        },
        // Error handling for mutations
        onError: (error: any) => {
          console.error('Mutation Error:', error);
          nrPageAction('queryClient', {
            message: `Mutation failed: ${error?.message || 'Unknown error'}`,
            url: window.location.href,
            error: error?.response?.status || 'unknown'
          });
        },
      },
    },
  });
};

// Export a default instance
export const queryClient = createQueryClient();